import type { ReactNode } from 'react';

export type Tone = 'ok' | 'info' | 'warn' | 'stop' | 'critical' | 'neutral';

/** Maps the API's severity and status vocabularies onto the visual tones. */
export function toneFor(value: string | null | undefined): Tone {
  switch (value) {
    case 'CRITICAL': return 'critical';
    case 'HIGH':
    case 'FAILED':
    case 'REJECTED':
    case 'LOST':
    case 'WIPED':
    case 'WIPE_PENDING':
    case 'OFFBOARDED': return 'stop';
    case 'MEDIUM':
    case 'PENDING_APPROVAL':
    case 'QUARANTINED':
    case 'EXPIRED':
    case 'SUSPENDED':
    case 'DRAFT': return 'warn';
    case 'LOW':
    case 'DISPATCHED':
    case 'ACKNOWLEDGED':
    case 'APPROVED':
    case 'OPEN': return 'info';
    case 'ACTIVE':
    case 'COMPLETED':
    case 'RESOLVED':
    case 'PUBLISHED': return 'ok';
    default: return 'neutral';
  }
}

export function Pill({ children, tone = 'neutral' }: { children: ReactNode; tone?: Tone }) {
  return <span className={`pill ${tone}`}>{children}</span>;
}

export function StatusPill({ value }: { value: string | null | undefined }) {
  if (!value) return <span className="pill neutral">unknown</span>;
  return <Pill tone={toneFor(value)}>{value.replace(/_/g, ' ').toLowerCase()}</Pill>;
}

export function Card({ title, extra, children }: { title?: string; extra?: ReactNode; children: ReactNode }) {
  return (
    <div className="card">
      {title && (
        <h2>
          {title}
          {extra && <span className="sub">{extra}</span>}
        </h2>
      )}
      {children}
    </div>
  );
}

export function Stat({
  label, value, note, tone,
}: { label: string; value: ReactNode; note?: ReactNode; tone?: 'good' | 'alarm' }) {
  return (
    <div className={`stat${tone ? ` ${tone}` : ''}`}>
      <div className="label">{label}</div>
      <p className="value">{value}</p>
      {note && <div className="note">{note}</div>}
    </div>
  );
}

export function Notice({ kind, children }: { kind: 'error' | 'warn' | 'ok' | 'info'; children: ReactNode }) {
  return <div className={`notice ${kind}`}>{children}</div>;
}

export function Empty({ children }: { children: ReactNode }) {
  return <div className="empty">{children}</div>;
}

export function Loading() {
  return <div className="loading">Loading&hellip;</div>;
}

/** A relative time for recency, which is what an operator actually reads a timestamp for. */
export function when(iso: string | null | undefined): string {
  if (!iso) return 'never';
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return 'unknown';

  const seconds = Math.round((Date.now() - date.getTime()) / 1000);
  if (seconds < 0) return date.toLocaleString();
  if (seconds < 60) return 'just now';
  if (seconds < 3600) return `${Math.floor(seconds / 60)} min ago`;
  if (seconds < 86_400) return `${Math.floor(seconds / 3600)} h ago`;
  if (seconds < 2_592_000) return `${Math.floor(seconds / 86_400)} d ago`;
  return date.toLocaleDateString();
}

export function exactly(iso: string | null | undefined): string {
  if (!iso) return 'never';
  const date = new Date(iso);
  return Number.isNaN(date.getTime()) ? 'unknown' : date.toLocaleString();
}

export function bytes(value: number | null | undefined): string {
  if (value === null || value === undefined) return 'unknown';
  const units = ['B', 'KB', 'MB', 'GB', 'TB'];
  let size = value;
  let unit = 0;
  while (size >= 1024 && unit < units.length - 1) {
    size /= 1024;
    unit += 1;
  }
  return `${size.toFixed(unit === 0 ? 0 : 1)} ${units[unit]}`;
}

export function duration(seconds: number | null | undefined): string {
  if (!seconds) return '0m';
  const hours = Math.floor(seconds / 3600);
  const minutes = Math.round((seconds % 3600) / 60);
  return hours > 0 ? `${hours}h ${minutes}m` : `${minutes}m`;
}
